import { useEffect, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  RFS12,
  SENTIENCE_AUTONOMY,
  SENTIENCE_CLOSING,
  SENTIENCE_HERO,
  SENTIENCE_LEGAL as L,
  SENTIENCE_PAGES,
  SENTIENCE_PILOT,
  SENTIENCE_PILOT_PATH,
  SENTIENCE_TEAM,
  SENTIENCE_VERBS,
  type SentiencePageDef,
} from "../../content/sentience-site";
import { PilotForm } from "./PilotForm";
import "./sentience.css";

const NAV_PAGES = SENTIENCE_PAGES.filter((p) => p.inNav);

function SubNav() {
  const here = useLocation().pathname.replace(/\/+$/, "") || "/";
  return (
    <nav className="sn-subnav" aria-label="Sentience">
      <Link to="/sentience" className="sn-subnav__brand">
        <img src="/brand/sentience-mark.png" alt="" width={24} height={24} />
        <b>Sentience</b>
      </Link>
      <ul>
        {NAV_PAGES.map((p) => (
          <li key={p.key}>
            <Link
              to={p.path}
              className={here === p.path ? "active" : undefined}
              aria-current={here === p.path ? "page" : undefined}
            >
              {p.navLabel}
            </Link>
          </li>
        ))}
      </ul>
      <Link className="btn btn--primary sn-subnav__cta" to={SENTIENCE_PILOT_PATH}>Start a Pilot</Link>
    </nav>
  );
}

function Head({ eyebrow, heading, lede }: { eyebrow: string; heading: string; lede?: string }) {
  return (
    <header className="sn-head">
      <p className="sn-eyebrow">{eyebrow}</p>
      <h2>{heading}</h2>
      {lede ? <p className="sn-lede">{lede}</p> : null}
    </header>
  );
}

function PageTitle({ eyebrow, heading, lede }: { eyebrow: string; heading: string; lede: string }) {
  return (
    <header className="sn-head sn-head--page">
      <p className="sn-eyebrow">{eyebrow}</p>
      <h1>{heading}</h1>
      <p className="sn-lede">{lede}</p>
    </header>
  );
}

function PilotCta({ quiet = false }: { quiet?: boolean }) {
  return (
    <Link className={quiet ? "btn btn--quiet" : "btn btn--primary"} to={SENTIENCE_PILOT_PATH}>
      {SENTIENCE_PILOT.cta}
    </Link>
  );
}

function verbPath(key: string) {
  return `/sentience/${key}`;
}

function Overview() {
  const h = SENTIENCE_HERO;
  return (
    <>
      <section className="sn-hero sn-hero--single">
        <div className="sn-hero__copy">
          <p className="sn-eyebrow">{h.eyebrow}</p>
          <h1>{h.headline}</h1>
          <p className="sn-lede">{h.lede}</p>
          <p className="sn-body">{h.autonomy}</p>
          <div className="sn-ctas">
            <Link className="btn btn--primary" to={h.primaryCta.href}>{h.primaryCta.label}</Link>
            <Link className="btn btn--quiet" to={verbPath("watch")}>See how it works</Link>
          </div>
        </div>
      </section>

      <section className="sn-section" aria-labelledby="sn-verbs">
        <Head eyebrow={`YC Request for Startups #12 · ${RFS12.label}`} heading="Four jobs, done for you" />
        <div className="sn-grid sn-grid--4" id="sn-verbs">
          {SENTIENCE_VERBS.map((v, i) => (
            <Link key={v.key} to={verbPath(v.key)} className="sn-card sn-card--link">
              <span className="sn-kicker">{String(i + 1).padStart(2, "0")}</span>
              <h3>{v.verb}</h3>
              <p>{v.you}</p>
              <b>Read →</b>
            </Link>
          ))}
        </div>
      </section>

      <section className="sn-section">
        <Head eyebrow="The difference" heading={SENTIENCE_TEAM.heading} lede={SENTIENCE_TEAM.body} />
        <div className="sn-compare">
          <div>
            <p className="sn-kicker">With a compliance team</p>
            <ol>{SENTIENCE_TEAM.without.map((s) => <li key={s}>{s}</li>)}</ol>
          </div>
          <div className="is-with">
            <p className="sn-kicker">With Sentience</p>
            <ol>{SENTIENCE_TEAM.with.map((s) => <li key={s}>{s}</li>)}</ol>
          </div>
        </div>
      </section>

      <Autonomy />
    </>
  );
}

function Autonomy() {
  const a = SENTIENCE_AUTONOMY;
  return (
    <section className="sn-section" aria-labelledby="sn-autonomy">
      <p className="sn-eyebrow">Autonomy</p>
      <h2 id="sn-autonomy">{a.heading}</h2>
      <p className="sn-lede">{a.body}</p>
      <ol className="sn-loop sn-loop--4">
        {a.levels.map((l, i) => (
          <li key={l.name}>
            <span className="sn-loop__name">{l.name.toUpperCase()}<small>{String(i + 1).padStart(2, "0")}</small></span>
            <p>{l.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function VerbPage({ verbKey }: { verbKey: (typeof SENTIENCE_VERBS)[number]["key"] }) {
  const index = SENTIENCE_VERBS.findIndex((v) => v.key === verbKey);
  const v = SENTIENCE_VERBS[index];
  if (!v) return null;
  return (
    <>
      <PageTitle eyebrow={`Sentience · ${String(index + 1).padStart(2, "0")} of 04`} heading={v.verb} lede={v.you} />
      <section className="sn-section sn-split">
        <div>
          <p className="sn-lede">{v.body}</p>
        </div>
        <ul className="sn-points">{v.points.map((p) => <li key={p}>{p}</li>)}</ul>
      </section>
      {verbKey === "file" ? <Autonomy /> : null}
    </>
  );
}

function Pilot() {
  const p = SENTIENCE_PILOT;
  return (
    <section className="sn-pilot">
      <div>
        <p className="sn-eyebrow">{p.eyebrow}</p>
        <h1>Apply for a Founding Pilot</h1>
        <p className="sn-lede">{p.lede}</p>
        <p className="sn-body">{p.outcome}</p>
        <div className="sn-scope">
          <p className="sn-kicker">Typical 30-day scope</p>
          <ul>{p.scope.map((s) => <li key={s}>{s}</li>)}</ul>
        </div>
        <div className="sn-card"><h3>What you get</h3><p>{p.deliverable}</p></div>
      </div>
      <div className="sn-pilot__form">
        <h2>{p.formHeading}</h2>
        <p className="sn-note">{p.formLede}</p>
        <PilotForm />
      </div>
    </section>
  );
}

function Legal({ title, intro, children }: { title: string; intro: string; children: ReactNode }) {
  return (
    <article className="sn-legal">
      <h1>{title}</h1>
      <p className="sn-note">Last updated {L.lastUpdated}</p>
      <p className="sn-lede">{intro}</p>
      {children}
    </article>
  );
}

const mail = <a href={`mailto:${L.email}`}>{L.email}</a>;

function Privacy() {
  return (
    <Legal title="Privacy" intro={`This notice covers the ${L.product} pages of odingard.com only: what happens to the information you send us through the pilot request form. It does not describe the ${L.product} application, which is governed by the agreement covering your pilot.`}>
      <h2>Who is responsible</h2>
      <p>{L.product} is a product of {L.entity}, which is responsible for the information collected through these pages. Privacy questions and requests go to {mail}.</p>
      <h2>What we collect</h2>
      <p>Only what you type into the pilot request form, plus the logs our hosting provider generates for every website:</p>
      <ul>{L.collected.map((c) => <li key={c}>{c}</li>)}</ul>
      <p>We do not use analytics, advertising, or tracking cookies on these pages, and we set no cookie to identify you. There is no account, no newsletter, and no behavioral profiling.</p>
      <h2>Why we collect it</h2>
      <p>To assess whether your compliance footprint is a fit for the {L.product} pilot program and to reply to you about it. That is the only purpose. We do not sell, rent, or share your information for anyone else’s marketing, and we do not add you to a mailing list because you enquired.</p>
      <p>Where data protection law requires a legal basis, ours is your consent — given by the checkbox on the form — and our legitimate interest in responding to a business enquiry you initiated.</p>
      <h2>Where it goes</h2>
      <p>Your submission is stored in a database and triggers an internal email so that a person sees it. The only third parties involved are:</p>
      <ul>{L.subprocessors.map((s) => <li key={s.name}><b>{s.name}</b> — {s.role}</li>)}</ul>
      <p>Both operate infrastructure in the United States, so your information is processed there. Nobody outside {L.entity} and these providers receives it.</p>
      <h2>How long we keep it</h2>
      <p>A pilot request that does not become a pilot is kept for {L.retention}, then deleted. If it does become a pilot, the agreement covering that pilot takes over.</p>
      <h2>Your choices</h2>
      <p>Ask us for a copy of what you sent, corrections to it, or its deletion, and we will act on it. Email {mail} from the address you submitted, or tell us which address to look for. Depending on where you live you may also have the right to object to processing, to restrict it, or to complain to your data protection authority.</p>
      <h2>Security</h2>
      <p>Submissions travel over TLS and are stored on managed infrastructure with access limited to the people who handle pilot requests. Please do not send credentials, API keys, regulatory filings, or personal data about anyone else through this form — it is a business contact form, and nothing more belongs in it.</p>
      <h2>Changes</h2>
      <p>If this notice changes we will update the date above. Material changes to how we use information you already sent us will be explained, not slipped in.</p>
    </Legal>
  );
}

function Terms() {
  return (
    <Legal title="Terms" intro={`These terms govern your use of the ${L.product} pages of odingard.com. They are not the terms of the ${L.product} product — using the software is governed by a separate written agreement.`}>
      <h2>What these pages are</h2>
      <p>Informational pages about {L.product}, published by {L.entity}. The contracting party for anything arising from them is {L.legalEntity}. Nothing here is an offer, a quotation, or a commitment to supply anything, and submitting the pilot request form does not enroll you in the pilot program or create a contract. Pilot participation begins only when both sides sign an agreement.</p>
      <h2>Not legal advice</h2>
      <p>{L.product} is compliance software. It is not a law firm, it does not practice law, and nothing on these pages — or produced by the product — is legal advice or a substitute for advice from a qualified professional in your jurisdiction. Regulatory determinations, filings and reports remain the responsibility of the licensed people and entities accountable for them.</p>
      <h2>Statements about the product</h2>
      <p>These pages describe {L.product} as a product, including capability that is planned rather than shipped. Planned capability is an intention, not a commitment: it may change, move, or not ship, and you should not buy, plan, or contract on the assumption that it will exist by a particular date. What is available to you is the scope written into your agreement with us, which controls over anything on these pages. In particular, external filing transmission requires recorded per-jurisdiction authority and is scoped with you before it is enabled.</p>
      <p>Product interfaces shown on these pages are illustrative and use demonstration data. They are labeled where they appear, and no figure in them describes a customer, an outcome, or a measured result.</p>
      <h2>Using the site</h2>
      <p>Read it, share it, quote it with attribution. Do not attempt to break it: no probing for vulnerabilities without written permission, no scraping that degrades the service, no submitting other people’s personal data through the form, and no using the form to send anything unlawful. We may block access that does any of these.</p>
      <p>If you believe you have found a security issue, tell us at {mail} rather than testing further.</p>
      <h2>Ownership</h2>
      <p>The {L.product} name, mark, page content, diagrams, and copy are owned by {L.entity}. Nothing here grants you a license to our trademarks or to the {L.product} software. Regulatory citations referenced in examples belong to the issuing authorities; naming a source does not imply endorsement by it.</p>
      <h2>No warranty</h2>
      <p>The site is provided as it is. We work to keep it accurate and available, but we do not warrant that it is free of errors or uninterrupted, and to the extent the law allows we disclaim implied warranties for the website itself. This does not limit any warranty we give you in a signed agreement for the product.</p>
      <h2>Liability</h2>
      <p>To the extent the law allows, {L.legalEntity} is not liable for indirect or consequential loss arising from your use of this website, or for decisions taken on the basis of information published here rather than agreed in writing. Nothing here excludes liability that cannot lawfully be excluded.</p>
      <h2>Changes and governing law</h2>
      <p>We may update these terms; the date above shows when we last did. Use of this website is governed by the laws of {L.jurisdiction}, without regard to conflict-of-laws rules. Questions go to {mail}.</p>
    </Legal>
  );
}


const BODIES: Record<SentiencePageDef["key"], () => ReactNode> = {
  overview: Overview,
  watch: () => <VerbPage verbKey="watch" />,
  file: () => <VerbPage verbKey="file" />,
  flag: () => <VerbPage verbKey="flag" />,
  report: () => <VerbPage verbKey="report" />,
  pilot: Pilot,
  privacy: Privacy,
  terms: Terms,
};

function NextPage({ current }: { current: SentiencePageDef }) {
  const index = NAV_PAGES.findIndex((p) => p.key === current.key);
  const next = index >= 0 ? NAV_PAGES[index + 1] : undefined;
  if (!next) return null;
  return (
    <Link className="sn-next" to={next.path}>
      <span>Next</span>
      <b>{next.navLabel} →</b>
    </Link>
  );
}

function Closing() {
  return (
    <section className="sn-closing">
      <h2>{SENTIENCE_CLOSING.headline}</h2>
      <p className="sn-lede">{SENTIENCE_CLOSING.body}</p>
      <p className="sn-closing__line">{SENTIENCE_CLOSING.line}</p>
      <div className="sn-ctas"><PilotCta /><Link className="btn btn--quiet" to="/contact?topic=sentience">Request a briefing</Link></div>
      <p className="sn-legal-links"><Link to="/sentience/privacy">Sentience privacy</Link> · <Link to="/sentience/terms">Sentience terms</Link></p>
    </section>
  );
}

export function SentiencePage({ page }: { page: SentiencePageDef }) {
  useEffect(() => {
    document.title = `${page.title} — Odingard`;
  }, [page]);
  const Body = BODIES[page.key];
  return (
    <main className="page sn" id="main">
      <SubNav />
      <Body />
      <NextPage current={page} />
      {page.key !== "pilot" ? <Closing /> : null}
    </main>
  );
}
